/**
 * Friendly schedules ↔ 5-field cron expressions (ADR-0062). Builders pick "every weekday at
 * 08:00"; the spec stores the cron expression Temporal runs (`config.schedule`) plus the friendly
 * plan (`config.plan`) so the picker can show it again. Next run times are computed in the
 * schedule's IANA time zone with `Intl` (no date library).
 */

export type Frequency = "daily" | "weekdays" | "weekly" | "monthly" | "hourly";
/** Sunday–Thursday (Saudi Arabia, Kuwait, Qatar, Bahrain, Oman) or Monday–Friday. */
export type WorkWeek = "sun-thu" | "mon-fri";

export interface SchedulePlan {
  frequency: Frequency;
  /** Local time `HH:MM` (for `hourly` only the minutes are used). */
  time: string;
  /** `weekly`: 0 = Sunday … 6 = Saturday. */
  weekday?: number;
  /** `monthly`: 1–28 (every month has these days). */
  monthDay?: number;
  /** `hourly`: run every N hours, starting at midnight. */
  everyHours?: number;
  /** `weekdays`: which working week. */
  workWeek?: WorkWeek;
}

export const EVERY_HOURS = [1, 2, 3, 4, 6, 8, 12] as const;
const SUN_THU_ZONES = new Set(["Asia/Riyadh", "Asia/Kuwait", "Asia/Qatar", "Asia/Bahrain", "Asia/Muscat", "Asia/Aden"]);

export function defaultWorkWeek(tz: string): WorkWeek {
  return SUN_THU_ZONES.has(tz) ? "sun-thu" : "mon-fri";
}

export function defaultPlan(tz = "Asia/Riyadh"): SchedulePlan {
  return { frequency: "weekdays", time: "08:00", workWeek: defaultWorkWeek(tz), weekday: 0, monthDay: 1, everyHours: 4 };
}

function hm(time: string): [number, number] {
  const m = /^(\d{1,2}):(\d{2})$/.exec(time.trim());
  if (!m) throw new Error(`invalid time ${time}`);
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) throw new Error(`invalid time ${time}`);
  return [h, min];
}

const pad = (n: number) => String(n).padStart(2, "0");

/** The cron expression for a plan. Throws on invalid input. */
export function planToCron(plan: SchedulePlan): string {
  const [h, m] = hm(plan.time);
  switch (plan.frequency) {
    case "daily":
      return `${m} ${h} * * *`;
    case "weekdays":
      return `${m} ${h} * * ${(plan.workWeek ?? "sun-thu") === "sun-thu" ? "0-4" : "1-5"}`;
    case "weekly": {
      const d = plan.weekday ?? 0;
      if (!Number.isInteger(d) || d < 0 || d > 6) throw new Error("weekday must be 0–6");
      return `${m} ${h} * * ${d}`;
    }
    case "monthly": {
      const d = plan.monthDay ?? 1;
      if (!Number.isInteger(d) || d < 1 || d > 28) throw new Error("day of month must be 1–28");
      return `${m} ${h} ${d} * *`;
    }
    case "hourly": {
      const n = plan.everyHours ?? 1;
      if (!(EVERY_HOURS as readonly number[]).includes(n)) throw new Error("every N hours must divide the day");
      return n === 1 ? `${m} * * * *` : `${m} */${n} * * *`;
    }
  }
}

const NUM = /^\d{1,2}$/;

/** The plan for a cron expression this picker can show, or null for anything else. */
export function cronToPlan(cron: string): SchedulePlan | null {
  const f = cron.trim().split(/\s+/);
  if (f.length !== 5) return null;
  const [mi, ho, dom, mon, dow] = f as [string, string, string, string, string];
  if (!NUM.test(mi) || Number(mi) > 59 || mon !== "*") return null;
  const minute = Number(mi);
  if (dom === "*" && dow === "*") {
    if (ho === "*") return { frequency: "hourly", time: `00:${pad(minute)}`, everyHours: 1 };
    const every = /^\*\/(\d{1,2})$/.exec(ho);
    if (every) {
      const n = Number(every[1]);
      return (EVERY_HOURS as readonly number[]).includes(n) ? { frequency: "hourly", time: `00:${pad(minute)}`, everyHours: n } : null;
    }
  }
  if (!NUM.test(ho) || Number(ho) > 23) return null;
  const time = `${pad(Number(ho))}:${pad(minute)}`;
  if (dom === "*" && dow === "*") return { frequency: "daily", time };
  if (dom === "*" && (dow === "0-4" || dow === "1-5")) return { frequency: "weekdays", time, workWeek: dow === "0-4" ? "sun-thu" : "mon-fri" };
  if (dom === "*" && /^[0-6]$/.test(dow)) return { frequency: "weekly", time, weekday: Number(dow) };
  if (dow === "*" && NUM.test(dom) && Number(dom) >= 1 && Number(dom) <= 28) return { frequency: "monthly", time, monthDay: Number(dom) };
  return null;
}

// ------------------------------------------------------------------ time-zone arithmetic

const formatters = new Map<string, Intl.DateTimeFormat>();
function partsFormatter(tz: string): Intl.DateTimeFormat {
  let f = formatters.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", { timeZone: tz, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" });
    formatters.set(tz, f);
  }
  return f;
}

interface Wall {
  y: number;
  mo: number;
  d: number;
  h: number;
  mi: number;
}

/** Wall-clock date and time of an instant in a time zone. */
export function wallClock(instant: number, tz: string): Wall {
  const p = Object.fromEntries(partsFormatter(tz).formatToParts(new Date(instant)).map((x) => [x.type, x.value]));
  return { y: Number(p.year), mo: Number(p.month), d: Number(p.day), h: Number(p.hour) % 24, mi: Number(p.minute) };
}

function offsetAt(instant: number, tz: string): number {
  const w = wallClock(instant, tz);
  const asUtc = Date.UTC(w.y, w.mo - 1, w.d, w.h, w.mi);
  return asUtc - Math.floor(instant / 60_000) * 60_000;
}

/**
 * The instant at which a wall-clock time occurs in a time zone, or null when that local time
 * does not exist (skipped by a daylight-saving change).
 */
export function zonedInstant(y: number, mo: number, d: number, h: number, mi: number, tz: string): number | null {
  const guess = Date.UTC(y, mo - 1, d, h, mi);
  let t = guess - offsetAt(guess, tz);
  t = guess - offsetAt(t, tz);
  const w = wallClock(t, tz);
  return w.y === y && w.mo === mo && w.d === d && w.h === h && w.mi === mi ? t : null;
}

function matchesDay(plan: SchedulePlan, y: number, mo: number, d: number): boolean {
  const dow = new Date(Date.UTC(y, mo - 1, d)).getUTCDay();
  switch (plan.frequency) {
    case "weekdays":
      return (plan.workWeek ?? "sun-thu") === "sun-thu" ? dow <= 4 : dow >= 1 && dow <= 5;
    case "weekly":
      return dow === (plan.weekday ?? 0);
    case "monthly":
      return d === (plan.monthDay ?? 1);
    default:
      return true;
  }
}

/** The next `count` run instants strictly after `from`, in the schedule's time zone. */
export function nextRuns(plan: SchedulePlan, tz: string, from: Date | number, count = 3): Date[] {
  const start = typeof from === "number" ? from : from.getTime();
  const [h, m] = hm(plan.time);
  const hours = plan.frequency === "hourly" ? Array.from({ length: 24 / (plan.everyHours ?? 1) }, (_, i) => i * (plan.everyHours ?? 1)) : [h];
  const today = wallClock(start, tz);
  const out: Date[] = [];
  for (let day = 0; day < 400 && out.length < count; day++) {
    const date = new Date(Date.UTC(today.y, today.mo - 1, today.d + day));
    const [y, mo, d] = [date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate()];
    if (!matchesDay(plan, y, mo, d)) continue;
    for (const hour of hours) {
      const t = zonedInstant(y, mo, d, hour, m, tz);
      if (t !== null && t > start) {
        out.push(new Date(t));
        if (out.length === count) break;
      }
    }
  }
  return out;
}

/** Common time zones for the override picker (the tenant's own is always offered too). */
export const COMMON_TIME_ZONES = [
  "Asia/Riyadh",
  "Asia/Dubai",
  "Asia/Kuwait",
  "Asia/Qatar",
  "Asia/Bahrain",
  "Asia/Muscat",
  "Africa/Cairo",
  "Asia/Amman",
  "Europe/London",
  "Europe/Paris",
  "America/New_York",
  "Asia/Singapore",
  "UTC",
] as const;
